import { parentApi } from '@mathflat/domain/Parent'
import type { Student } from '@mathflat/domain/Student'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { loadInitialData } from '~/@app/init/loadInitialData'
import { routeName } from '~/@common/constants'
import authService from '~/@common/services/auth.service'
import { localStorageService } from '~/@common/services/storage.service'
import { toastService } from '~/@common/services/toast.service'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import maxios from '~/@common/utils/maxios'

// 네이버 웍스(웨일스페이스) OAuth 2.0 authorize 엔드포인트 및 파라미터
// 외부 SSO 서버 스펙이므로 절대 임의로 변경하지 말 것
const NAVER_WORKS_AUTHORIZE_URL = 'https://auth.worksmobile.com/oauth2/v2.0/authorize'
const RESPONSE_TYPE = 'code'
const CLIENT_ID = import.meta.env.VITE_WHALESPACE_CLIENTID
const SCOPE = 'openid profile email user.whalespace.read'

// 콜백 URL의 path 는 반드시 /whale/callback 과 일치해야 함 (파트너사 console 에 등록된 redirect_uri)
// hostname 은 항상 student.* 로 고정한다.
// - student-up.* 도메인도 student로 callbackOrigin를 보내서 로그인부터 student를 이용할 수 있도록 함
const getRedirectUri = () => {
  const { origin, hostname } = window.location
  const callbackOrigin = hostname.startsWith('student-up')
    ? origin.replace('student-up', 'student')
    : origin
  return `${callbackOrigin}${routeName.auth.aiOnOAuthCallback}`
}

const buildAuthURI = (): string => {
  const params = new URLSearchParams()
  params.set('response_type', RESPONSE_TYPE)
  params.set('client_id', CLIENT_ID)
  // state는 CSRF 방어용 무작위 값. 시연용으로 timestamp 사용 (old 앱과 동일 로직 유지)
  params.set('state', Date.now().toString())
  params.set('redirect_uri', getRedirectUri())
  params.set('scope', SCOPE)
  return `${NAVER_WORKS_AUTHORIZE_URL}?${params.toString()}`
}

/**
 * 네이버 웍스(웨일스페이스) SSO 로그인 훅.
 *
 * 반환값:
 * - authURI: SSO 인가 요청 URL. 버튼 클릭 또는 redirect로 이동시키는 데 사용.
 * - login(code): 콜백에서 받은 code로 서버 로그인 후 역할별 홈으로 이동.
 * - isLoading: 로그인 진행 상태 (버튼 비활성화 등에 사용).
 *
 * 역할 분기 (주의: response.userId의 의미가 role마다 다름):
 * - TEACHER: 학생앱 사용 불가 → 토스트 후 /login으로 복귀
 * - STUDENT: response.userId === studentId. auth.service.loginByNaverWorks가
 *            이미 loadInitialData(userId)까지 처리했으므로 여기서는 이동만
 * - PARENT:  response.userId === parentId (≠ studentId). 학부모-학생 관계를
 *            조회해 studentId를 얻은 뒤 loadInitialData 실행 필요.
 *
 * ⚠️ 현재 STUDENT/PARENT 처리가 비대칭인 것은 의도된 것이 아니라, auth.service가
 *    @app을 import하는 레이어 역전 때문입니다. 추후 sessionService(authUser vs
 *    viewingStudent 분리)로 리팩토링 예정 — Login.page/GuestLogin.page와 동일한 패턴.
 *
 * redirectURI 쿼리가 있으면 최종 목적지에 전달 (deep link 지원).
 */
export const useNaverWorksLogin = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectURI = searchParams.get('redirectURI') ?? undefined

  const [isLoading, setIsLoading] = useState(false)

  const authURI = buildAuthURI()

  // 에러 발생 시 토스트 + 로그인 페이지로 복귀
  const handleError = (
    message = '웨일스페이스 로그인을 진행할 수 없습니다.\n다시 시도해주세요.',
  ) => {
    toastService.error(message)
    navigate(routeName.login, { replace: true })
  }

  const login = async (code: string) => {
    setIsLoading(true)
    try {
      const user = await authService.loginByNaverWorks(code, true)

      // x-platform 헤더를 실제 userType에 맞게 갱신 (API 권한 분기에 사용)
      maxios.defaults.headers.common['x-platform'] = user.userType

      // 선생님 계정은 학생앱에 로그인 불가 — 이미 저장된 JWT 세션 정리 후 리다이렉트
      if (user.userType === 'TEACHER') {
        await authService.logout()
        handleError('학생 계정으로 로그인해주세요.')
        return
      }

      // redirectURI가 있으면 쿼리스트링으로 목적지에 전달
      const queryString = redirectURI ? `?redirectURI=${encodeURIComponent(redirectURI)}` : ''

      if (user.userType === 'PARENT') {
        // PARENT: response.userId는 parentId라 그대로 loadInitialData에 쓸 수 없음.
        // 관계 조회로 studentId를 얻어서 초기화해야 함.
        try {
          const { studentIds } = await parentApi.getParentChildren(user.userId)
          const studentId = studentIds[0] as Student['id']
          localStorageService.update({ user: { studentId } })
          await loadInitialData(studentId)
        } catch {
          // 학부모-학생 관계 조회 실패 시 로그아웃 처리
          await authService.logout()
          return
        }
        navigate(getEntrancePage('PARENT') + queryString, { replace: true })
        return
      }

      // STUDENT: response.userId가 곧 studentId이므로 auth.service.loginByNaverWorks
      //          내부에서 loadInitialData(userId)까지 이미 완료됨. 여기선 이동만.
      navigate(getEntrancePage('STUDENT') + queryString, { replace: true })
    } catch {
      handleError()
    } finally {
      setIsLoading(false)
    }
  }

  return { authURI, login, isLoading }
}
